import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    Smartphone,
    Bell,
    ListOrdered,
    BarChart3,
    Hand,
    Fingerprint,
    ArrowRight,
    CheckCircle2,
    Download,
} from 'lucide-react';

const features = [
    {
        icon: Smartphone,
        title: 'iOS & Android',
        description:
            'Available on both iOS and Android devices. Download from the App Store or Google Play and start trading in minutes.',
    },
    {
        icon: Bell,
        title: 'Push Notifications',
        description:
            'Stay informed with real-time push notifications for price alerts, order executions, margin calls, and market events.',
    },
    {
        icon: ListOrdered,
        title: 'Full Order Management',
        description:
            'Complete order management with market, limit, stop, and stop-limit orders. Modify take profit and stop loss on the go.',
    },
    {
        icon: BarChart3,
        title: 'Real-Time Charts',
        description:
            'Professional charting on mobile with multiple timeframes, technical indicators, and drawing tools -- all optimized for touch screens.',
    },
    {
        icon: Hand,
        title: 'Touch-Friendly',
        description:
            'An intuitive touch-optimized interface designed for mobile trading. Swipe, pinch, and tap to navigate charts and manage orders effortlessly.',
    },
    {
        icon: Fingerprint,
        title: 'Biometric Login',
        description:
            'Secure your account with fingerprint or Face ID authentication. Fast, convenient access without compromising on security.',
    },
];

const benefits = [
    'Trade from anywhere with a mobile connection',
    'Real-time charts with technical indicators',
    'Instant push notifications and price alerts',
    'Synchronized with web and desktop platforms',
    'Secure biometric authentication',
    'Optimized for both phones and tablets',
];

export default function Mobile() {
    return (
        <AppLayout title={`${siteConfig.platform.shortName} Mobile - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="bg-background pt-32 lg:pt-40 pb-20">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Mobile Platform</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            {siteConfig.platform.name} <span className="text-gradient">Mobile</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
                            Trade On The Go
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.3}>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    <Download className="mr-2 h-4 w-4" />
                                    Get Started
                                </Button>
                            </a>
                            <a href={siteConfig.platform.downloadUrl} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" variant="outline">
                                    Download App
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                        </div>
                    </FadeIn>
                </div>
            </Section>

            {/* Features */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Mobile"
                        highlight="Features"
                        description="Everything you need to trade professionally, optimized for mobile devices."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div key={feature.title} variants={staggerItem}>
                                    <Card className="h-full rounded-xl border border-border bg-card/80 backdrop-blur-sm transition-colors hover:border-primary/20">
                                        <CardContent className="p-6 lg:p-8">
                                            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                <Icon className="h-5 w-5 text-primary" />
                                            </div>
                                            <h3 className="mb-2 text-lg font-semibold text-foreground">
                                                {feature.title}
                                            </h3>
                                            <p className="text-sm leading-relaxed text-muted-foreground">
                                                {feature.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Benefits */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Why Choose"
                        highlight={`${siteConfig.platform.shortName} Mobile`}
                        description={`Never miss a trading opportunity with ${siteConfig.platform.shortName} Mobile in your pocket.`}
                    />
                </FadeIn>
                <FadeIn delay={0.1}>
                    <div className="max-w-2xl mx-auto">
                        <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                            <CardContent className="p-8">
                                <StaggerChildren>
                                    <ul className="space-y-4">
                                        {benefits.map((benefit) => (
                                            <motion.li
                                                key={benefit}
                                                variants={staggerItem}
                                                className="flex items-center gap-3 text-muted-foreground"
                                            >
                                                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 border border-primary/10 shrink-0">
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                                </div>
                                                <span>{benefit}</span>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </StaggerChildren>
                            </CardContent>
                        </Card>
                    </div>
                </FadeIn>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Download <span className="text-gradient">{siteConfig.platform.shortName} Mobile</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            Get {siteConfig.platform.name} on your mobile device and trade from anywhere. Available on iOS and Android.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    <Download className="mr-2 h-4 w-4" />
                                    Get Started
                                </Button>
                            </a>
                            <a href={siteConfig.platform.downloadUrl} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" variant="outline">
                                    Download App
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
